import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { featureTemplateService } from "@/services/featureTemplateService";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "../../../supabase/supabase";
import { Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface FormData {
  name: string;
  agent_id: string;
  schema_template_id: string;
  max_instances?: number;
  max_leads_per_instance?: number;
  max_call_minutes_per_instance?: number;
  max_reports_per_instance?: number;
}

interface Assistant {
  id: string;
  name: string;
}

interface SchemaTemplate {
  id: string;
  name: string;
}

interface FeatureTemplateFormProps {
  organizationId: string;
  onComplete: () => void;
  initialData?: FormData;
  disableSchemaTemplate?: boolean; // New prop
}

export function FeatureTemplateForm({
  organizationId,
  onComplete,
  initialData,
  disableSchemaTemplate = false, // Default to false
}: FeatureTemplateFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [schemaTemplates, setSchemaTemplates] = useState<SchemaTemplate[]>([]);

  const form = useForm<FormData>({
    defaultValues: {
      name: initialData?.name || "",
      agent_id: initialData?.agent_id || "",
      schema_template_id: initialData?.schema_template_id || "",
      max_instances: initialData?.max_instances,
      max_leads_per_instance: initialData?.max_leads_per_instance,
      max_call_minutes_per_instance: initialData?.max_call_minutes_per_instance,
      max_reports_per_instance: initialData?.max_reports_per_instance,
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch assistants
        const { data: assistantsData, error: assistantsError } = await supabase
          .from("assistants")
          .select("id, name")
          .eq("organization_id", organizationId);

        if (assistantsError) throw assistantsError;
        setAssistants(assistantsData || []);

        // Fetch schema templates
        const { data: templatesData, error: templatesError } = await supabase
          .from("schema_templates")
          .select("id, name")
          .eq("organization_id", organizationId);

        if (templatesError) throw templatesError;
        setSchemaTemplates(templatesData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to load required data",
          variant: "destructive",
        });
      }
    };

    fetchData();
  }, [organizationId]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("User not authenticated");

      // Convert empty strings to null for optional number fields
      const processedData = {
        ...data,
        max_instances: data.max_instances || null,
        max_leads_per_instance: data.max_leads_per_instance || null,
        max_call_minutes_per_instance:
          data.max_call_minutes_per_instance || null,
        max_reports_per_instance: data.max_reports_per_instance || null,
      };

      if (initialData) {
        await featureTemplateService.updateFeatureTemplate(
          initialData.agent_id,
          {
            ...processedData,
            organization_id: organizationId,
          },
        );
      } else {
        await featureTemplateService.createFeatureTemplate({
          ...processedData,
          organization_id: organizationId,
          created_by: userData.user.id,
        });
      }

      toast({
        title: "Success",
        description: `Feature template ${initialData ? "updated" : "created"} successfully`,
      });
      onComplete();
    } catch (error) {
      console.error("Error saving feature template:", error);
      toast({
        title: "Error",
        description: `Failed to ${initialData ? "update" : "create"} feature template`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="limits">Usage Limits</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 mt-4">
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter template name" {...field} />
                  </FormControl>
                  <FormDescription>
                    A descriptive name for your feature template
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="agent_id"
              rules={{ required: "Agent is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agent</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an agent" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {assistants.map((assistant) => (
                        <SelectItem key={assistant.id} value={assistant.id}>
                          {assistant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    The agent that will handle this feature
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="schema_template_id"
              rules={{ required: "Schema template is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Schema Template</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={disableSchemaTemplate} // Add disabled prop
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a schema template" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {schemaTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    The schema template that defines the data structure
                    {disableSchemaTemplate &&
                      " (cannot be changed after creation)"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>

          <TabsContent value="limits" className="space-y-4 mt-4">
            <FormField
              control={form.control}
              name="max_instances"
              rules={{ min: { value: 0, message: "Must be 0 or greater" } }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maximum Instances</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="No limit"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value
                          ? parseInt(e.target.value)
                          : undefined;
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    Maximum number of instances that can be created from this
                    template. Leave empty for no limit.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_leads_per_instance"
              rules={{ min: { value: 0, message: "Must be 0 or greater" } }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maximum Leads per Instance</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="No limit"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value
                          ? parseInt(e.target.value)
                          : undefined;
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    Maximum number of leads allowed per instance. Leave empty
                    for no limit.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_call_minutes_per_instance"
              rules={{ min: { value: 0, message: "Must be 0 or greater" } }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maximum Call Minutes per Instance</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="No limit"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value
                          ? parseInt(e.target.value)
                          : undefined;
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    Maximum total call minutes allowed per instance. Leave empty
                    for no limit.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_reports_per_instance"
              rules={{ min: { value: 0, message: "Must be 0 or greater" } }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maximum Reports per Instance</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="No limit"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value
                          ? parseInt(e.target.value)
                          : undefined;
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    Maximum number of reports that can be generated per
                    instance. Leave empty for no limit.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {initialData ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>{initialData ? "Update" : "Create"} Template</>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
